/* eslint-disable @compiled/no-keyframes-tagged-template-expression, @atlaskit/design-system/no-keyframes-tagged-template-expression, @atlaskit/ui-styling-standard/no-unsafe-values */
import React, { memo } from 'react';

import { cssMap as compiledCssMap, keyframes } from '@compiled/react';
import { Box } from '@atlaskit/primitives/compiled/box';
import { ResourcedEmoji } from '@atlaskit/emoji';
import type { EmojiProvider } from '@atlaskit/emoji';

import { RESOURCED_EMOJI_COMPACT_HEIGHT } from './emojiConfig.ts';
import type { AnimatingEmojiData } from './useBloomAnimation.ts';

const bloomKeyframes = keyframes`
	0% {
		opacity: 1;
		transform: translate(0, 0) scale(1) rotate(0deg);
	}
	20% {
		opacity: 1;
		transform: translate(0, -50px) scale(1.5) rotate(var(--bloom-tilt, 0deg));
	}
	100% {
		opacity: 0;
		transform: translate(var(--bloom-drift, 0px), var(--bloom-distance, -120px)) scale(0.5) rotate(var(--bloom-rotation, 0deg));
	}
`;

const styles = compiledCssMap({
	bloomContainer: {
		position: 'fixed',
		transform: 'translate(-50%, -50%)',
		pointerEvents: 'none',
		animationName: bloomKeyframes,
		animationDuration: '1.3s',
		animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
		animationFillMode: 'forwards',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
});

interface BloomAnimationEmojiProps {
	animatingEmoji: AnimatingEmojiData;
	emojiProvider: Promise<EmojiProvider>;
	emojiSize?: number;
}

const BloomAnimationEmojiInner: React.FC<BloomAnimationEmojiProps> = ({
	animatingEmoji,
	emojiProvider,
	emojiSize = RESOURCED_EMOJI_COMPACT_HEIGHT,
}) => {
	return (
		<Box
			xcss={styles.bloomContainer}
			style={
				{
					left: `${animatingEmoji.x}px`,
					top: `${animatingEmoji.y}px`,
					'--bloom-drift': animatingEmoji.bloomDrift,
					'--bloom-tilt': animatingEmoji.bloomTilt,
					'--bloom-rotation': animatingEmoji.bloomRotation,
					'--bloom-distance': animatingEmoji.bloomDistance,
				} as React.CSSProperties
			}
		>
			<ResourcedEmoji
				emojiProvider={emojiProvider}
				emojiId={{ id: animatingEmoji.emojiId, shortName: '' }}
				showTooltip={false}
				fitToHeight={emojiSize}
			/>
		</Box>
	);
};

export const BloomAnimationEmoji: React.NamedExoticComponent<BloomAnimationEmojiProps> =
	memo(BloomAnimationEmojiInner);
BloomAnimationEmoji.displayName = 'BloomAnimationEmoji';
